import { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';

import { viewStaffForStudy } from '../../../../../../services/PersonService';
import { logMissedVisit } from '../../../../../../services/ParticipantService';

import { SaveButton } from '../../../../../common/button/save-button/SaveButton';
import { Checkbox } from '../../../../../common/checkbox/Checkbox';
import Modal from '../../../../../common/modal/Modal';

import './MissedVisitCapture.css';

const MissedVisitCapture = ({
  context,
  participant,
  show,
  onCancelClick,
  setSpinnerText,
  showToaster,
  retrieveParticipant,
  close,
}) => {
  const [protocolDeviation, setProtocolDeviation] = useState(false);
  const [missedVisitCRF, setMissedVisitCRF] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const retrieveStaff = useCallback(async () => {
    setSpinnerText('Retrieving Staff');

    const requestData = {
      participantExternalReference: participant.externalReference,
    };

    const [data, error] = await viewStaffForStudy(context.username, requestData);

    if (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
      setSpinnerText(null);
      return;
    }

    if (data.responseStatus !== 'Success') {
      showToaster(data);
      setSpinnerText(null);
      return;
    }

    setUsers(data.viewAllPersonSystemUserVOs);
    setSpinnerText(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.username, participant.externalReference]);

  const availableUsers = useMemo(
    () =>
      users.filter(
        ({ externalReference }) => !selectedUsers.some((user) => user.externalReference === externalReference)
      ),
    [selectedUsers, users]
  );

  useEffect(() => {
    if (!show) return;

    retrieveStaff();
  }, [retrieveStaff, show]);

  if (!show) return null;

  const removeUser = (user) => {
    const fileteredUsers = selectedUsers.filter(
      ({ externalReference }) => externalReference !== user.externalReference
    );
    setSelectedUsers(fileteredUsers);
  };

  const handleSaveClick = async () => {
    setSpinnerText('Saving Missed Visit');

    const username = context.username;
    const participantExternalReference = participant.externalReference;

    const requestData = {
      notifyUsers: selectedUsers.map(({ externalReference }) => externalReference),
      visitCode: participant.captureVisit.visitCode,
      participantExternalReference,
      protocolDeviation,
      missedVisitCRF,
    };

    const [data, error] = await logMissedVisit(username, requestData);

    if (error) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
      setSpinnerText(null);
      return;
    }

    showToaster(data);

    if (data.responseStatus !== 'Success') {
      setSpinnerText(null);
      return;
    }

    await retrieveParticipant(username, participantExternalReference);

    close();
  };

  const renderUsers = () => {
    if (!selectedUsers.length) return null;

    return (
      <div className="missed-visit-capture-users">
        {selectedUsers.map((user) => (
          <div key={user.externalReference} className="missed-visit-capture-user">
            <p>{user.name}</p>
            <button className="missed-visit-capture-remove-user-button" onClick={() => removeUser(user)}>
              X
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal headerTitle="Missed Visit" justifyContent="center" contentPadding="5px 15px" width="600px" hideButton>
      <p>
        Please confirm if a Protocol Deviation or Missed Visit CRF is required and select the individuals to be
        notified:
      </p>

      <div className="missed-visit-capture-inputs">
        <div className="missed-visit-capture-input">
          <label className="input-label">Protocol Deviation:</label>
          <Checkbox checked={protocolDeviation} onChange={setProtocolDeviation} color="#6F8695" fillStyle={false} />
        </div>

        <div className="missed-visit-capture-input">
          <label className="input-label">Missed Visit CRF:</label>
          <Checkbox checked={missedVisitCRF} onChange={setMissedVisitCRF} color="#6F8695" fillStyle={false} />
        </div>

        <div className="missed-visit-capture-input">
          <label className="input-label">Notify User(s):</label>
          <div className="missed-visit-capture-users-input">
            {renderUsers()}
            <Select
              options={availableUsers}
              onChange={(option) => setSelectedUsers((prev) => [...prev, option])}
              className="input-select"
              classNamePrefix="input-select"
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              value={null}
              closeMenuOnSelect={false}
            />
          </div>
        </div>
      </div>

      <div className="missed-visit-capture-buttons">
        <SaveButton
          textColor="#077187"
          border="1px solid #077187"
          title="Cancel"
          onClick={onCancelClick}
          color="#ffffff"
        />
        <SaveButton
          textColor="#ffffff"
          border="1px solid #077187"
          title="Save"
          onClick={handleSaveClick}
          color="#077187"
        />
      </div>
    </Modal>
  );
};

export default MissedVisitCapture;
